
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { User } from '../../types/user.types'
import type { ConfirmationChannel } from '../../types/user.types'
import type { RegistrationPath } from '../../types/user.types'
import * as authApi from '../../api/auth.api'

interface AuthState {
    user: User | null
    token: string | null
    isAuthenticated: boolean
    registrationPending: boolean
    registrationChannel: ConfirmationChannel | null
    registrationDestinationHint: string | null
    registrationNeedsEmail: boolean
    registrationNeedsTelegram: boolean
    registrationTelegramDeepLink: string | null
    registrationPath: RegistrationPath | null
    pendingRegistrationEmail: string | null
    pendingRegistrationPhone: string | null
    loading: boolean
    error: string | null
}

const initialState: AuthState = {
    user: JSON.parse(localStorage.getItem('user') || 'null'),
    token: localStorage.getItem('authToken'),
    isAuthenticated: !!localStorage.getItem('authToken'),
    registrationPending: false,
    registrationChannel: null,
    registrationDestinationHint: null,
    registrationNeedsEmail: false,
    registrationNeedsTelegram: false,
    registrationTelegramDeepLink: null,
    registrationPath: null,
    pendingRegistrationEmail: null,
    pendingRegistrationPhone: null,
    loading: false,
    error: null
}

function applyRegistrationPayload(state: AuthState, payload: authApi.RegisterResponse, meta?: { email?: string; phone?: string }) {
    state.registrationPending = payload.requiresConfirmation || !!payload.needsTelegramVerification
    state.registrationChannel = payload.channel ?? null
    state.registrationDestinationHint = payload.destinationHint ?? null
    state.registrationNeedsEmail = payload.needsEmailConfirmation ?? false
    state.registrationNeedsTelegram = payload.needsTelegramVerification ?? false
    state.registrationTelegramDeepLink = payload.telegramDeepLink ?? null
    if (meta?.email) state.pendingRegistrationEmail = meta.email
    if (meta?.phone) state.pendingRegistrationPhone = meta.phone
    if (payload.authResponse) {
        state.user = payload.authResponse.user
        state.token = payload.authResponse.token
        state.isAuthenticated = true
        localStorage.setItem('authToken', payload.authResponse.token)
        localStorage.setItem('user', JSON.stringify(payload.authResponse.user))
    }
}

export const login = createAsyncThunk(
    'auth/login',
    async (
        { emailOrPhone, password }: { emailOrPhone: string; password: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await authApi.login(emailOrPhone, password)
            localStorage.setItem('authToken', response.token)
            localStorage.setItem('user', JSON.stringify(response.user))
            return response
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Login failed')
        }
    }
)

export const loginLookup = createAsyncThunk(
    'auth/loginLookup',
    async (emailOrPhone: string, { rejectWithValue }) => {
        try {
            return await authApi.loginLookup(emailOrPhone)
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Lookup failed')
        }
    }
)

export const startActivation = createAsyncThunk(
    'auth/startActivation',
    async (emailOrPhone: string, { rejectWithValue }) => {
        try {
            return await authApi.startActivation(emailOrPhone)
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Activation start failed')
        }
    }
)

export const resendActivation = createAsyncThunk(
    'auth/resendActivation',
    async (emailOrPhone: string, { rejectWithValue }) => {
        try {
            return await authApi.resendActivation(emailOrPhone)
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message || 'Resend failed')
        }
    }
)

export const confirmActivation = createAsyncThunk(
    'auth/confirmActivation',
    async (
        payload: {
            token: string
            password: string
            confirmPassword: string
            birthDate?: string
            photoUrl?: string
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await authApi.confirmActivation(payload)
            localStorage.setItem('authToken', response.token)
            localStorage.setItem('user', JSON.stringify(response.user))
            return response
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Activation failed'
            )
        }
    }
)

export const register = createAsyncThunk(
    'auth/register',
    async (
        payload: {
            email?: string | null
            phone?: string | null
            password: string
            fullName: string
            confirmationChannel?: ConfirmationChannel
            registrationPath?: RegistrationPath
            personalDataConsent?: boolean
            locale?: 'en' | 'ua'
            telegramInviteToken?: string
        },
        { rejectWithValue }
    ) => {
        try {
            const response = await authApi.register(payload)
            return { response, email: payload.email ?? null, phone: payload.phone ?? null, path: payload.registrationPath ?? null }
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Registration failed'
            )
        }
    }
)

export const registerSocial = createAsyncThunk(
    'auth/registerSocial',
    async (
        payload: { provider: 'google' | 'apple'; authorizationCode: string; redirectUri: string; locale?: 'en' | 'ua' },
        { rejectWithValue }
    ) => {
        try {
            return await authApi.socialRegister(payload)
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Social registration failed'
            )
        }
    }
)

export const loginSocial = createAsyncThunk(
    'auth/loginSocial',
    async (
        payload: { provider: 'google' | 'apple'; authorizationCode: string; redirectUri: string },
        { rejectWithValue }
    ) => {
        try {
            const response = await authApi.socialLogin(payload)
            localStorage.setItem('authToken', response.token)
            localStorage.setItem('user', JSON.stringify(response.user))
            return response
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Social login failed'
            )
        }
    }
)

export const resendRegistration = createAsyncThunk(
    'auth/resendRegistration',
    async (payload: { email?: string; phone?: string }, { rejectWithValue }) => {
        try {
            return await authApi.resendRegistration(payload)
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Resend failed'
            )
        }
    }
)

export const refreshRegistrationStatus = createAsyncThunk(
    'auth/refreshRegistrationStatus',
    async (_, { rejectWithValue }) => {
        try {
            return await authApi.getRegistrationStatus()
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || error.message)
        }
    }
)

export const confirmRegistration = createAsyncThunk(
    'auth/confirmRegistration',
    async (token: string, { rejectWithValue }) => {
        try {
            const response = await authApi.confirmRegistration(token)
            localStorage.setItem('authToken', response.token)
            localStorage.setItem('user', JSON.stringify(response.user))
            return response
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Confirmation failed'
            )
        }
    }
)

export const completeProfile = createAsyncThunk(
    'auth/completeProfile',
    async (
        payload: {
            fullName?: string
            email?: string
            birthDate?: string
            photoUrl?: string
            phone?: string
        },
        { rejectWithValue }
    ) => {
        try {
            const user = await authApi.completeProfile(payload)
            localStorage.setItem('user', JSON.stringify(user))
            return user
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Profile completion failed'
            )
        }
    }
)

export const sendPhoneConfirmation = createAsyncThunk(
    'auth/sendPhoneConfirmation',
    async (channel: ConfirmationChannel, { rejectWithValue }) => {
        try {
            return await authApi.sendPhoneConfirmation(channel)
        } catch (error: any) {
            return rejectWithValue(
                error.response?.data?.message || error.message || 'Sending confirmation failed'
            )
        }
    }
)

export const logout = createAsyncThunk('auth/logout', async () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('user')
})

export const refreshUser = createAsyncThunk(
    'auth/refreshUser',
    async (_, { rejectWithValue }) => {
        try {
            const user = await authApi.getCurrentUser()
            localStorage.setItem('user', JSON.stringify(user))
            return user
        } catch (error: any) {
            return rejectWithValue(error.message)
        }
    }
)

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null
        },
        clearRegistrationPending: (state) => {
            state.registrationPending = false
            state.registrationChannel = null
            state.registrationDestinationHint = null
            state.registrationNeedsEmail = false
            state.registrationNeedsTelegram = false
            state.registrationTelegramDeepLink = null
            state.registrationPath = null
            state.pendingRegistrationEmail = null
            state.pendingRegistrationPhone = null
        },
        setUser: (state, action) => {
            state.user = action.payload
            state.isAuthenticated = true
        }
    },
    extraReducers: (builder) => {
        builder.addCase(login.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(login.fulfilled, (state, action) => {
            state.user = action.payload.user
            state.token = action.payload.token
            state.isAuthenticated = true
            state.loading = false
            state.error = null
        })
        builder.addCase(login.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
            state.isAuthenticated = false
        })

        builder.addCase(loginLookup.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(loginLookup.fulfilled, (state) => {
            state.loading = false
            state.error = null
        })
        builder.addCase(loginLookup.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(startActivation.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(startActivation.fulfilled, (state) => {
            state.loading = false
            state.error = null
        })
        builder.addCase(startActivation.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(resendActivation.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(resendActivation.fulfilled, (state) => {
            state.loading = false
            state.error = null
        })
        builder.addCase(resendActivation.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(confirmActivation.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(confirmActivation.fulfilled, (state, action) => {
            state.user = action.payload.user
            state.token = action.payload.token
            state.isAuthenticated = true
            state.loading = false
            state.error = null
        })
        builder.addCase(confirmActivation.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(register.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(register.fulfilled, (state, action) => {
            applyRegistrationPayload(state, action.payload.response, {
                email: action.payload.email ?? undefined,
                phone: action.payload.phone ?? undefined,
            })
            state.registrationPath = action.payload.path ?? null
            state.loading = false
            state.error = null
        })
        builder.addCase(register.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(registerSocial.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(registerSocial.fulfilled, (state, action) => {
            applyRegistrationPayload(state, action.payload)
            state.registrationPath = 'EMAIL'
            state.registrationNeedsTelegram = action.payload.needsTelegramVerification ?? true
            state.loading = false
            state.error = null
        })
        builder.addCase(registerSocial.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(loginSocial.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(loginSocial.fulfilled, (state, action) => {
            state.user = action.payload.user
            state.token = action.payload.token
            state.isAuthenticated = true
            state.loading = false
            state.error = null
        })
        builder.addCase(loginSocial.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(resendRegistration.fulfilled, (state, action) => {
            applyRegistrationPayload(state, action.payload)
            state.loading = false
        })

        builder.addCase(refreshRegistrationStatus.fulfilled, (state, action) => {
            if (action.payload.telegramDeepLink) {
                state.registrationTelegramDeepLink = action.payload.telegramDeepLink
            }
            state.registrationNeedsEmail = !action.payload.emailVerified
            state.registrationNeedsTelegram = !action.payload.phoneVerified
            if (action.payload.emailVerified && action.payload.phoneVerified) {
                state.registrationPending = false
            }
        })

        builder.addCase(confirmRegistration.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(confirmRegistration.fulfilled, (state, action) => {
            state.user = action.payload.user
            state.token = action.payload.token
            state.isAuthenticated = true
            state.registrationNeedsEmail = false
            state.registrationNeedsTelegram = !action.payload.user.phoneVerified
            state.registrationPending = state.registrationNeedsTelegram
            state.loading = false
            state.error = null
        })
        builder.addCase(confirmRegistration.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(completeProfile.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(completeProfile.fulfilled, (state, action) => {
            state.user = action.payload
            state.loading = false
            state.error = null
        })
        builder.addCase(completeProfile.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })

        builder.addCase(logout.fulfilled, (state) => {
            state.user = null
            state.token = null
            state.isAuthenticated = false
            state.registrationPending = false
            state.registrationChannel = null
            state.registrationDestinationHint = null
            state.registrationNeedsEmail = false
            state.registrationNeedsTelegram = false
            state.registrationTelegramDeepLink = null
            state.registrationPath = null
            state.pendingRegistrationEmail = null
            state.pendingRegistrationPhone = null
            state.error = null
        })

        builder.addCase(refreshUser.fulfilled, (state, action) => {
            state.user = action.payload
        })

        builder.addCase(sendPhoneConfirmation.pending, (state) => {
            state.loading = true
            state.error = null
        })
        builder.addCase(sendPhoneConfirmation.fulfilled, (state, action) => {
            applyRegistrationPayload(state, action.payload)
            state.loading = false
            state.error = null
        })
        builder.addCase(sendPhoneConfirmation.rejected, (state, action) => {
            state.loading = false
            state.error = action.payload as string
        })
    }
})

export const { clearError, clearRegistrationPending, setUser } = authSlice.actions
export default authSlice.reducer
